import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Mail, Bell, MessageSquare, Edit2, Save, X, ToggleLeft, ToggleRight } from "lucide-react";
import api from "../../lib/api";


const channelIcons = { email: Mail, sms: MessageSquare, "in-app": Bell };

const NotificationsPage = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/notification-templates");
      setTemplates(res.data.templates || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (tmpl) => {
    setEditing(tmpl.id);
    setEditForm({ name: tmpl.name, channel: tmpl.channel, subject: tmpl.subject, body: tmpl.body, is_active: tmpl.is_active });
  };

  const saveEdit = async (id) => {
    try {
      await api.patch("/notification-templates/${id}", editForm);
      toast.success("Template updated");
      setEditing(null);
      fetchData();
    } catch (e) { toast.error("Failed to update"); }
  };

  const toggleActive = async (tmpl) => {
    try {
      await api.patch("/notification-templates/${tmpl.id}", { is_active: !tmpl.is_active });
      toast.success(tmpl.is_active ? "Template disabled" : "Template enabled");
      fetchData();
    } catch (e) { toast.error("Failed to toggle"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Notification Templates">
    <div className="p-6 space-y-6" data-testid="notifications-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Notification Templates</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage email, SMS, and in-app notification templates by trigger type</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #1B4F8A" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Total Templates</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{templates.length}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #0E8A55" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Active</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{templates.filter(t => t.is_active).length}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #C7322A" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Disabled</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{templates.filter(t => !t.is_active).length}</p>
        </div>
      </div>

      {/* Template Cards */}
      <div className="space-y-3">
        {templates.map(tmpl => {
          const ChannelIcon = channelIcons[tmpl.channel] || Mail;
          const isEditing = editing === tmpl.id;

          return (
            <div key={tmpl.id} className="bg-background border rounded-xl p-4 shadow-sm" data-testid={`notif-template-${tmpl.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tmpl.is_active ? 'bg-blue-100 text-blue-800' : 'bg-muted text-muted-foreground'}`}>
                    <ChannelIcon className="w-5 h-5" />
                  </div>
                  <div>
                    {isEditing ? (
                      <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="h-8 text-sm font-bold w-64" />
                    ) : (
                      <h3 className="font-bold text-sm">{tmpl.name}</h3>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">Trigger: <code className="bg-muted px-1 rounded">{tmpl.trigger}</code></p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(tmpl)} className="p-1" data-testid={`toggle-notif-${tmpl.id}`}>
                    {tmpl.is_active ? <ToggleRight className="w-6 h-6 text-green-600" /> : <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                  </button>
                  {isEditing ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => saveEdit(tmpl.id)} className="h-8"><Save className="w-4 h-4 mr-1" />Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(null)} className="h-8"><X className="w-4 h-4" /></Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => startEdit(tmpl)} className="h-8" data-testid={`edit-notif-${tmpl.id}`}><Edit2 className="w-4 h-4" /></Button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="mt-3 space-y-2 border-t pt-3">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-xs font-medium">Subject</label>
                      <Input value={editForm.subject} onChange={e => setEditForm({...editForm, subject: e.target.value})} className="h-8 text-sm mt-1" />
                    </div>
                    <div className="w-32">
                      <label className="text-xs font-medium">Channel</label>
                      <select className="w-full mt-1 h-8 rounded-md border px-2 text-sm bg-background" value={editForm.channel} onChange={e => setEditForm({...editForm, channel: e.target.value})}>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="in-app">In-App</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Body</label>
                    <textarea className="w-full mt-1 rounded-md border p-2 text-sm bg-background min-h-[80px] resize-y" value={editForm.body} onChange={e => setEditForm({...editForm, body: e.target.value})} />
                  </div>
                  <p className="text-xs text-muted-foreground">Available variables: {"{{learner_name}}, {{course_name}}, {{score}}, {{cert_number}}, {{due_date}}, {{session_title}}"}</p>
                </div>
              ) : (
                <div className="mt-2 text-sm">
                  <p className="text-muted-foreground"><span className="font-medium text-foreground">Subject:</span> {tmpl.subject}</p>
                  <p className="text-muted-foreground text-xs mt-1 line-clamp-2">{tmpl.body}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    </AdminLayout>
  );
};

export default NotificationsPage;
